import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { Locale } from '~/stores/app/types';

import { ApiMethod, handleApiError } from '~/utils/apiClient';
import { FetchState } from '~/utils/common';

import type { Store, StoreFetchParams } from './types';

const STORE_ID = 'store';

export const STORES_PAGE_LIMIT = 10;

interface Actions {
  fetchStores: (params: StoreFetchParams & { locale: Locale }) => Promise<void>;
}

interface Getters extends _GettersTree<State> {
  totalPages: (state: State) => number;
}

interface State {
  currentPage: number;
  storeFetchState: FetchState;
  stores: Store[];
  total: number;
}

export const useStoreStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async fetchStores({ locale, ...params }) {
      this.storeFetchState = FetchState.IN_PROGRESS;
      try {
        const response = await this.$api.fetch<Store[]>(ApiMethod.GET, '/stores', {
          limit: STORES_PAGE_LIMIT,
          locale,
          ...params,
        });

        if (response.data) {
          this.stores = response.data;
          this.currentPage = params.page ?? 1;
          this.total = response.meta?.pagination?.total ?? response.data.length;
          this.storeFetchState = FetchState.SUCCEEDED;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);
        this.storeFetchState = FetchState.ERRORED;
      }
    },
  },
  getters: {
    totalPages: (state): number => Math.ceil(state.total / STORES_PAGE_LIMIT),
  },
  state: () => ({
    currentPage: 1,
    storeFetchState: FetchState.INITIAL,
    stores: [],
    total: 0,
  }),
});
